<template>
  <main class="model-prices-page">
    <section class="soft-card table-panel">
      <div class="toolbar">
        <el-input
          v-model="searchQuery"
          placeholder="搜索模型名称..."
          size="small"
          clearable
          class="search-input"
          @keyup.enter.native="onSearch" @clear="onSearch"
        />
        <el-button size="small" plain icon="el-icon-search" @click="onSearch">搜索</el-button>
      </div>
      <div class="desktop-only">
        <el-table ref="modelPriceTable" :data="models" size="small" style="width: 100%">
          <el-table-column type="index" label="序号" width="60" :index="pageIndex" key="idx" />
          <el-table-column v-if="showCustomerColumn" label="客户" width="140" show-overflow-tooltip key="customer">
            <template slot-scope="scope">{{ scope.row.customerName || '-' }}</template>
          </el-table-column>
          <el-table-column v-if="showSupplierColumn" label="供应商" width="120" show-overflow-tooltip key="supplier">
            <template slot-scope="scope">
              <span>{{ scope.row.supplierName || '-' }}</span>
              <el-tag v-if="scope.row.priority === 1" size="mini" type="success" style="margin-left:4px;">主用</el-tag>
              <el-tag v-else-if="scope.row.priority" size="mini" type="warning" style="margin-left:4px;">备{{ scope.row.priority }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="模型名称" min-width="240" key="model">
            <template slot-scope="scope">
              <div class="model-cell">
                <span class="model-type-badge" :class="modelTypeInfo(scope.row.modelType).badgeShort">{{ modelTypeInfo(scope.row.modelType).short }}</span>
                <span class="model-icon">
                  <img v-if="scope.row.modelIcon" :src="scope.row.modelIcon" :alt="scope.row.name" @error="scope.row.modelIcon = ''">
                  <span v-else>{{ modelAbbr(scope.row.name) }}</span>
                </span>
                <div class="model-main">
                  <div class="model-main-row">
                    <span class="model-name" :title="scope.row.name">{{ scope.row.name }}</span>
                  </div>
                  <div v-if="scope.row.offlineAt" class="model-offline-row">
                    <el-tag size="mini" :type="offlineTagType(scope.row)">{{ offlineTagText(scope.row) }}</el-tag>
                    <span>{{ offlineAtText(scope.row.offlineAt) }}</span>
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="输入价格" width="150" key="in">
            <template slot-scope="scope">
              <span v-if="scope.row.modelType === 'IMAGE' || scope.row.modelType === 'VIDEO'" class="price-value dash">-</span>
              <span v-else class="price-value">¥ {{ formatPrice(scope.row.customerInputPrice) }}<span class="price-unit">{{ modelTypeInfo(scope.row.modelType).unitShort }}</span></span>
            </template>
          </el-table-column>
          <el-table-column label="输出价格" width="190" key="out">
            <template slot-scope="scope">
              <span v-if="scope.row.modelType === 'EMBEDDING' || scope.row.modelType === 'RERANK' || scope.row.modelType === 'ASR' || scope.row.modelType === 'TTS'" class="price-value dash">-</span>
              <span v-else-if="scope.row.modelType === 'VIDEO'" class="price-value video-price-lines">
                <template v-if="videoPriceList(scope.row).length">
                  <span v-for="item in videoPriceList(scope.row)" :key="item.resolution" class="video-price-line">
                    {{ item.resolution }} ¥ {{ formatPrice(item.outputPrice) }} / 秒
                  </span>
                </template>
                <span v-else>¥ {{ formatPrice(scope.row.customerOutputPrice) }} / 秒</span>
              </span>
              <span v-else class="price-value">¥ {{ formatPrice(scope.row.customerOutputPrice) }}<span class="price-unit">{{ scope.row.modelType === 'IMAGE' ? ' / 张' : ' / 1K' }}</span></span>
            </template>
          </el-table-column>
          <el-table-column label="缓存命中价格" width="160" key="cache">
            <template slot-scope="scope">
              <span v-if="scope.row.modelType === 'IMAGE' || scope.row.modelType === 'VIDEO' || scope.row.modelType === 'EMBEDDING' || scope.row.modelType === 'RERANK' || scope.row.modelType === 'ASR' || scope.row.modelType === 'TTS'" class="price-value dash">-</span>
              <span v-else class="price-value">¥ {{ formatPrice(scope.row.customerCachePrice) }}<span class="price-unit"> / 1K</span></span>
            </template>
          </el-table-column>
          <el-table-column label="分时段价格" min-width="320" key="slot">
            <template slot-scope="scope">
              <div v-if="scope.row.modelType === 'TEXT' && scope.row.timePrices && scope.row.timePrices.length" class="slot-block">
                <div class="slot-line">闲时：入 <b>{{ formatPrice(scope.row.customerInputPrice) }}</b> / 出 <b>{{ formatPrice(scope.row.customerOutputPrice) }}</b> / 缓 <b>{{ formatPrice(scope.row.customerCachePrice) }}</b></div>
                <div v-for="(slot, i) in scope.row.timePrices" :key="i" class="slot-line busy">
                  <span class="slot-time">{{ minutesToHHmm(slot.startMinute) }}-{{ minutesToHHmm(slot.endMinute, true) }}</span>入 <b>{{ formatPrice(slot.inputPrice) }}</b> / 出 <b>{{ formatPrice(slot.outputPrice) }}</b> / 缓 <b>{{ formatPrice(slot.cachePrice) }}</b>
                </div>
              </div>
              <span v-else class="price-value dash">-</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="mobile-only list-cards">
        <div class="list-card" v-for="(row, index) in models" :key="row.id || index">
          <div class="card-header">
            <div class="model-cell" style="display:inline-flex;align-items:center;gap:8px;">
              <span class="model-type-badge" :class="modelTypeInfo(row.modelType).badgeShort">{{ modelTypeInfo(row.modelType).short }}</span>
              <span class="model-icon" style="width:24px;height:24px;font-size:11px;">
                <img v-if="row.modelIcon" :src="row.modelIcon" :alt="row.name" @error="row.modelIcon = ''">
                <span v-else>{{ modelAbbr(row.name) }}</span>
              </span>
              <div>
                <div class="card-title">{{ row.name }}
                <el-tag v-if="row.offlineAt" size="mini" :type="offlineTagType(row)" style="margin-left:6px;">{{ offlineTagText(row) }}</el-tag>
                <span v-if="row.offlineAt" style="margin-left:6px;font-size:12px;color:var(--muted);">{{ offlineAtText(row.offlineAt) }}</span>
              </div>
                <div v-if="showSupplierColumn || row.customerName" class="card-sub">
                  <template v-if="showSupplierColumn">
                    {{ row.supplierName || '-' }}
                    <template v-if="row.priority === 1"> · 主用</template>
                    <template v-else-if="row.priority"> · 备用优先{{ row.priority }}</template>
                  </template>
                  <template v-if="showCustomerColumn && row.customerName"> · {{ row.customerName }}</template>
                </div>
              </div>
            </div>
          </div>
          <div class="price-grid" v-if="row.modelType === 'IMAGE'">
            <div class="price-cell">
              <div class="label">每张价格</div>
              <div class="value output">¥ {{ formatPrice(row.customerOutputPrice) }}</div>
            </div>
          </div>
          <div class="price-grid" v-else-if="row.modelType === 'VIDEO'">
            <div v-if="videoPriceList(row).length" class="price-cell" style="flex:1;">
              <div class="label">分辨率价格</div>
              <div v-for="item in videoPriceList(row)" :key="item.resolution" class="value output">
                {{ item.resolution }} ¥ {{ formatPrice(item.outputPrice) }} / 秒
              </div>
            </div>
            <div v-else class="price-cell">
              <div class="label">每秒价格</div>
              <div class="value output">¥ {{ formatPrice(row.customerOutputPrice) }} / 秒</div>
            </div>
          </div>
          <div v-else-if="row.modelType === 'EMBEDDING' || row.modelType === 'RERANK' || row.modelType === 'ASR' || row.modelType === 'TTS'" class="price-grid">
            <div class="price-cell">
              <div class="label">输入{{ row.modelType === 'ASR' ? '（元/千秒）' : (row.modelType === 'TTS' ? '（元/千字符）' : '（元/1K）') }}</div>
              <div class="value input">¥ {{ formatPrice(row.customerInputPrice) }}</div>
            </div>
          </div>
          <div class="price-grid" v-else>
            <div class="price-cell">
              <div class="label">输入</div>
              <div class="value input">¥ {{ formatPrice(row.customerInputPrice) }}</div>
            </div>
            <div class="price-cell">
              <div class="label">输出</div>
              <div class="value output">¥ {{ formatPrice(row.customerOutputPrice) }}</div>
            </div>
            <div class="price-cell">
              <div class="label">缓存</div>
              <div class="value cache">¥ {{ formatPrice(row.customerCachePrice) }}</div>
            </div>
          </div>
          <div v-if="row.modelType === 'TEXT' && row.timePrices && row.timePrices.length" class="slot-block mobile">
            <div class="slot-line">闲时：入 <b>{{ formatPrice(row.customerInputPrice) }}</b> / 出 <b>{{ formatPrice(row.customerOutputPrice) }}</b> / 缓 <b>{{ formatPrice(row.customerCachePrice) }}</b></div>
            <div v-for="(slot, i) in row.timePrices" :key="i" class="slot-line busy">
              <span class="slot-time">{{ minutesToHHmm(slot.startMinute) }}-{{ minutesToHHmm(slot.endMinute, true) }}</span>入 <b>{{ formatPrice(slot.inputPrice) }}</b> / 出 <b>{{ formatPrice(slot.outputPrice) }}</b> / 缓 <b>{{ formatPrice(slot.cachePrice) }}</b>
            </div>
          </div>
        </div>
      </div>
      <div class="legend-row">
        <span class="legend-item"><span class="legend-badge txt">文</span>文本</span>
        <span class="legend-item"><span class="legend-badge img">图</span>图片</span>
        <span class="legend-item"><span class="legend-badge vid">视</span>视频</span>
        <span class="legend-item"><span class="legend-badge emb">向</span>向量</span>
        <span class="legend-item"><span class="legend-badge rrm">排</span>重排</span>
        <span class="legend-item"><span class="legend-badge spk">语</span>语音识别</span>
        <span class="legend-item"><span class="legend-badge tts">合</span>语音合成</span>
      </div>
      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="total"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="onPageChange"
      />
    </section>
  </main>
</template>

<script>
import { modelTypeInfo as mtInfo } from '../utils/format'
export default {
  name: 'ModelPricesPage',
  data() {
    return {
      models: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      searchQuery: ''
    }
  },
  computed: {
    pageIndex() {
      return (this.currentPage - 1) * this.pageSize + 1
    },
    showCustomerColumn() {
      return this.models.some(item => item.customerName || item.customerCode)
    },
    showSupplierColumn() {
      return this.models.some(item => item.customerName || item.customerCode)
    }
  },
  mounted() {
    this.fetchModels()
  },
  methods: {
    modelTypeInfo(type) {
      return mtInfo(type)
    },
    videoPriceList(row) {
      if (row.videoPrices && row.videoPrices.length) return row.videoPrices
      if (row.supplierVideoPrices && row.supplierVideoPrices.length) return row.supplierVideoPrices
      return []
    },
    modelAbbr(name) {
      if (!name) return 'M'
      return name.substring(0, 2).toUpperCase()
    },
    formatPrice(value) {
      if (value == null) return '-'
      return new Intl.NumberFormat('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6
      }).format(value)
    },
    minutesToHHmm(minutes, isEnd = false) {
      if (minutes == null) return ''
      const total = Number(minutes)
      if (total === 1440) return '24:00'
      if (total < 0 || total >= 24 * 60) return ''
      const h = String(Math.floor(total / 60)).padStart(2, '0')
      const m = String(total % 60).padStart(2, '0')
      return h + ':' + m
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchModels()
    },
    offlineTagType(row) {
      if (!row.offlineAt) return 'success'
      return new Date(row.offlineAt).getTime() <= Date.now() ? 'danger' : 'warning'
    },
    offlineTagText(row) {
      if (!row.offlineAt) return ''
      return new Date(row.offlineAt).getTime() <= Date.now() ? '已下架' : '即将下架'
    },
    offlineAtText(val) {
      if (!val) return ''
      return String(val).slice(0, 10)
    },
    onSearch() {
      this.currentPage = 1
      this.fetchModels()
    },
    async fetchModels() {
      try {
        const res = await this.$http.get('/admin/model-prices', {
          params: {
            keyword: this.searchQuery || undefined,
            page: this.currentPage,
            pageSize: this.pageSize
          }
        })
        this.models = res.records || []
        this.total = Number(res.total) || 0
        await this.$nextTick()
        if (this.$refs.modelPriceTable) {
          this.$refs.modelPriceTable.doLayout()
        }
      } catch {
        this.$message.error('加载模型价格失败')
      }
    }
  }
}
</script>

<style scoped>
.model-prices-page {
  padding: 18px 24px 76px;
}

.table-panel {
  padding: 16px;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

.table-panel ::v-deep .el-table,
.table-panel ::v-deep .el-table__header-wrapper,
.table-panel ::v-deep .el-table__body-wrapper {
  border-radius: var(--radius);
}

.table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 850;
}

.table-panel ::v-deep td,
.table-panel ::v-deep th {
  border-color: var(--line-soft);
}

.legend-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 18px;
  margin-top: 14px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  font-size: 12px;
  color: var(--muted);
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.legend-badge {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
}

.legend-badge.txt {
  color: #32c8f5;
  background: rgba(50, 200, 245, 0.14);
  border: 1px solid rgba(50, 200, 245, 0.28);
}

.legend-badge.img {
  color: #f4b74f;
  background: rgba(244, 183, 79, 0.14);
  border: 1px solid rgba(244, 183, 79, 0.28);
}

.legend-badge.vid {
  color: #f87171;
  background: rgba(248, 113, 113, 0.14);
  border: 1px solid rgba(248, 113, 113, 0.28);
}

.legend-badge.emb {
  color: #10b981;
  background: rgba(16, 185, 129, 0.14);
  border: 1px solid rgba(16, 185, 129, 0.28);
}

.legend-badge.rrm {
  color: #f472b6;
  background: rgba(244, 114, 182, 0.14);
  border: 1px solid rgba(244, 114, 182, 0.28);
}

.legend-badge.spk {
  color: #a78bfa;
  background: rgba(167, 139, 250, 0.14);
  border: 1px solid rgba(167, 139, 250, 0.28);
}

.legend-badge.tts {
  color: #34d399;
  background: rgba(52, 211, 153, 0.14);
  border: 1px solid rgba(52, 211, 153, 0.28);
}

.pagination-bar {
  margin-top: 20px;
  text-align: right;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
}

.search-input {
  width: 220px;
}

.muted-text {
  font-size: 12px;
  color: var(--muted-2);
}

.model-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.model-main {
  flex: 1;
  min-width: 0;
}

.model-main-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.model-type-badge {
  flex: none;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
}

.model-type-badge.txt {
  color: #32c8f5;
  background: rgba(50, 200, 245, 0.14);
  border: 1px solid rgba(50, 200, 245, 0.28);
}

.model-type-badge.img {
  color: #f4b74f;
  background: rgba(244, 183, 79, 0.14);
  border: 1px solid rgba(244, 183, 79, 0.28);
}

.model-type-badge.vid {
  color: #f87171;
  background: rgba(248, 113, 113, 0.14);
  border: 1px solid rgba(248, 113, 113, 0.28);
}

.model-type-badge.emb {
  color: #10b981;
  background: rgba(16, 185, 129, 0.14);
  border: 1px solid rgba(16, 185, 129, 0.28);
}

.model-type-badge.rrm {
  color: #f472b6;
  background: rgba(244, 114, 182, 0.14);
  border: 1px solid rgba(244, 114, 182, 0.28);
}

.model-type-badge.spk {
  color: #a78bfa;
  background: rgba(167, 139, 250, 0.14);
  border: 1px solid rgba(167, 139, 250, 0.28);
}

.model-type-badge.tts {
  color: #34d399;
  background: rgba(52, 211, 153, 0.14);
  border: 1px solid rgba(52, 211, 153, 0.28);
}

.model-offline-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 12px;
  color: var(--muted);
}

.model-icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  overflow: hidden;
  color: var(--cyan);
  font-size: 12px;
  font-weight: 800;
  background: rgba(79, 134, 255, 0.14);
  border: 1px solid rgba(133, 164, 255, 0.16);
}

.model-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.model-name {
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.price-value {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--line-soft);
  font-weight: 700;
  color: var(--text);
}

.price-value.dash {
  color: var(--muted-2);
  border-color: transparent;
}

.price-unit {
  font-weight: 400;
  font-size: 11px;
  color: var(--muted-2);
  margin-left: 2px;
}

.video-price-lines {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 8px;
}

.video-price-line {
  font-size: 12px;
  line-height: 1.35;
  white-space: nowrap;
}

.slot-block {
  display: grid;
  gap: 6px;
  line-height: 1.6;
  font-size: 12px;
  color: var(--muted);
}

.slot-line {
  padding: 5px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.04);
}

.slot-line b {
  font-weight: 700;
  color: var(--text);
}

.slot-line.busy {
  background: rgba(244, 183, 79, 0.08);
}

.slot-line.busy b {
  color: #f4b74f;
}

.slot-time {
  display: inline-block;
  margin-right: 8px;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(244, 183, 79, 0.15);
  color: #f4b74f;
  font-weight: 700;
}

.slot-block.mobile {
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
}

.list-cards {
  display: grid;
  gap: 14px;
}

.mobile-only { display: none; }

@media (max-width: 760px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .model-prices-page {
    padding: 12px 12px 28px;
  }
  .table-panel {
    padding: 10px;
    overflow-x: visible;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .search-input {
    flex: 1;
    min-width: 80px;
  }
  .pagination-bar {
    text-align: center;
  }
  .slot-block .slot-line {
    padding: 2px 0;
    border-radius: 0;
    background: transparent;
  }
  .slot-block .slot-line.busy {
    background: transparent;
  }
}
.list-card {
  padding: 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(27,39,58,.92), rgba(20,30,46,.88));
  border: 1px solid rgba(133,164,255,.12);
  box-shadow: 0 8px 28px rgba(0,0,0,.22);
  overflow: hidden;
  transition: transform .18s ease, box-shadow .18s ease;
}
.list-card:active {
  transform: translateY(-1px);
  box-shadow: 0 12px 36px rgba(0,0,0,.3);
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 8px;
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
}
.card-sub {
  font-size: 12px;
  color: rgba(255,255,255,.4);
  margin-top: 2px;
}
.price-grid {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
.price-cell {
  flex: 1;
  min-width: 0;
  text-align: center;
  padding: 8px 6px;
  border-radius: 8px;
  background: rgba(255,255,255,.04);
}
.price-cell .label {
  font-size: 10px;
  color: rgba(255,255,255,.35);
  letter-spacing: .02em;
}
.price-cell .value {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  margin-top: 2px;
}
.price-cell .value.input { color: #32c8f5; }
.price-cell .value.output { color: #f4b74f; }
.price-cell .value.cache { color: #34c98f; }
.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(133,164,255,0.06);
  margin-top: 6px;
  font-size: 11px;
  color: rgba(255,255,255,.35);
}
.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid rgba(133,164,255,0.06);
  margin-top: 6px;
}
.card-actions .el-button--text {
  font-size: 12px;
  padding: 4px 10px;
  height: auto;
}
</style>
